import React, { useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Badge from "@amzn/awsui-components-core/badge";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Container from "@amzn/awsui-components-core/container";
import FormField from "@amzn/awsui-components-core/form-field";
import Icon from "@amzn/awsui-components-core/icon";
import Input from "@amzn/awsui-components-core/input";
import Modal from "@amzn/awsui-components-core/modal";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import ActionButtons from 'src/components/shared/ActionButtons';
import RichTextEditor from 'src/components/shared/RichTextEditor';
import RichTextContent from 'src/components/shared/RichTextContent';
import { fetchShiftPassdown, createShiftPassdown, updateShiftPassdown, deleteShiftPassdown, ShiftPassdownEntry, CreateShiftPassdownInput } from 'src/services/passdowns-api';
import { useUser } from 'src/context/UserContext';
import { saveDraft, loadDraft, clearDraft } from 'src/utils/formDraft';
import FormattedDate from 'src/components/shared/FormattedDate';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { formatPlainTextToHtml } from 'src/utils/helpers';
import { MESSAGES } from 'src/utils/errorMessages';
import { passdownHistoryStyles } from 'src/components/shared/design-tokens';
import mockData from 'src/mock/mockData.json';

const DRAFT_KEY = 'passdown-cs';
const USE_MOCK_DATA = false;

/** Strips HTML tags to test whether rich-text content is actually present. */
const richTextToPlain = (html?: string) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

interface CSFormData {
    category: string;
    openCaseIds: string;
    csEscalations: string;
    csBOLOs: string;
    welfareChecks: string;
    noteworthyCases: string;
    abandonedCalls: number;
    avgSpeedOfAnswer: number;
    // Tiptap ProseMirror JSON mirrors of the rich-text fields above.
    openCaseIdsJson?: Record<string, any>;
    csEscalationsJson?: Record<string, any>;
    csBOLOsJson?: Record<string, any>;
    welfareChecksJson?: Record<string, any>;
    noteworthyCasesJson?: Record<string, any>;
}

const defaultForm: CSFormData = {
    category: 'CS',
    openCaseIds: '',
    csEscalations: '',
    csBOLOs: '',
    welfareChecks: '',
    noteworthyCases: '',
    abandonedCalls: 0,
    avgSpeedOfAnswer: 0,
};

const CSPassdown = ({ showForm, setShowForm }: { showForm: boolean; setShowForm: (v: boolean) => void }) => {
    // Only Admin/Super Author (any record) and the record owner (own records) can edit/delete CS passdowns
    const { canModify } = useUser();
    const [passdowns, setPassdowns] = useState<ShiftPassdownEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [activeExpanded, setActiveExpanded] = useState(true);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [form, setForm] = useState<CSFormData>({ ...defaultForm });
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; createdBy: string } | null>(null);

    const updateField = (field: keyof CSFormData, value: string | number | Record<string, any>) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        loadPassdowns();
        const draft = loadDraft(DRAFT_KEY);
        if (draft) {
            setForm({ ...defaultForm, ...draft });
            setEditingId(draft.editingId || null);
            setShowForm(true);
            clearDraft(DRAFT_KEY);
        }
    }, []);

    const loadPassdowns = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setPassdowns((mockData.cspassdowns?.items || []) as ShiftPassdownEntry[]);
            } else {
                const data = await fetchShiftPassdown('CS');
                setPassdowns(data.items || []);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (b: ShiftPassdownEntry) => {
        setEditingId(b.passdownId);
        setForm({
            category: 'CS',
            openCaseIds: b.openCaseIds || '',
            csEscalations: b.csEscalations || '',
            csBOLOs: b.csBOLOs || '',
            welfareChecks: b.welfareChecks || '',
            noteworthyCases: b.noteworthyCases || '',
            abandonedCalls: b.abandonedCalls || 0,
            avgSpeedOfAnswer: b.avgSpeedOfAnswer || 0,
            openCaseIdsJson: b.openCaseIdsJson,
            csEscalationsJson: b.csEscalationsJson,
            csBOLOsJson: b.csBOLOsJson,
            welfareChecksJson: b.welfareChecksJson,
            noteworthyCasesJson: b.noteworthyCasesJson,
        });
        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ ...defaultForm });
        clearDraft(DRAFT_KEY);
    };

    const handleDeleteClick = (b: ShiftPassdownEntry) => {
        setDeleteTarget({ id: b.passdownId, createdBy: b.createdBy });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteShiftPassdown(deleteTarget.id);
            setSuccessMsg('CS Passdown deleted');
            await loadPassdowns();
        } catch (err: any) {
            setError(MESSAGES.RECORD_DELETE_ERROR);
        }
        setDeleteTarget(null);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const input: CreateShiftPassdownInput = { ...form };
            const isUpdate = !!editingId;
            if (editingId) { await updateShiftPassdown(editingId, input); }
            else { await createShiftPassdown(input); }
            resetForm();
            setSuccessMsg(isUpdate ? 'CS Passdown updated' : 'CS Passdown created');
            await loadPassdowns();
        } catch (err: any) {
            const msg = err.message || '';
            if (msg.includes('Not authenticated') || msg.includes('403')) {
                saveDraft(DRAFT_KEY, { ...form, editingId });
            }
            setError(editingId ? MESSAGES.RECORD_UPDATE_ERROR : MESSAGES.RECORD_CREATE_ERROR);
        } finally { setSubmitting(false); }
    };

    // The only non-rich CS fields are numeric (abandoned calls, avg speed of
    // answer), so this renders a plain numeric value — no dangerouslySetInnerHTML.
    const renderField = (label: string, value?: number) => {
        if (!value && value !== 0) return null;
        return (
            <div className="passdown-entry__field">
                <h3>{label}</h3>
                <p>{value}</p>
            </div>
        );
    };

    // Render a CS rich-text field read-only. Prefers the Tiptap JSON when present
    // (rendered safely via RichTextContent); otherwise falls back to the legacy
    // plain-text string so older records still render as before.
    const renderRichField = (label: string, json?: Record<string, any>, value?: string, exactCase = false) => {
        // `value` is the plain-text mirror of the JSON (both are emitted together by
        // the editor and co-persisted), so it reliably reflects whether the field has
        // real content — including when the editor was cleared to an empty document
        // (which still emits a JSON doc). Hide empty fields, matching the original
        // renderField behavior.
        if (!richTextToPlain(value)) return null;
        return (
            <div className="passdown-entry__field">
                <h3 style={exactCase ? { textTransform: 'none' } : undefined}>{label}</h3>
                <div className="passdown-rich-text">
                    <RichTextContent json={json as any} html={formatPlainTextToHtml(value || '')} />
                </div>
            </div>
        );
    };

    const hasContent = !!richTextToPlain(form.openCaseIds) || !!richTextToPlain(form.csEscalations) || !!richTextToPlain(form.noteworthyCases);
    const b0 = passdowns[0];

    return (
        <>
            <div ref={alertRef} tabIndex={-1}>
                { error &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                }
                { successMsg &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="success"
                            onDismiss={() => setSuccessMsg(null)}
                        >
                            {successMsg}
                        </Alert>
                    </div>
                }
            </div>

            {showForm && (
                <div className="passdown-form">
                    <FormField label="Open case IDs and next steps for closure" stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            autoFocus
                            value={form.openCaseIdsJson ?? formatPlainTextToHtml(form.openCaseIds || '')}
                            placeholder="Open cases and next steps..."
                            onChangeText={(text) => updateField('openCaseIds', text)}
                            onChangeJson={(json) => updateField('openCaseIdsJson', json)}
                        />
                    </FormField>

                    <FormField label="Escalations during shift and resolution" stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.csEscalationsJson ?? formatPlainTextToHtml(form.csEscalations || '')}
                            placeholder="Escalations and resolutions..."
                            onChangeText={(text) => updateField('csEscalations', text)}
                            onChangeJson={(json) => updateField('csEscalationsJson', json)}
                        />
                    </FormField>

                    <FormField label="BOLOs" stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.csBOLOsJson ?? formatPlainTextToHtml(form.csBOLOs || '')}
                            placeholder="BOLOs..."
                            onChangeText={(text) => updateField('csBOLOs', text)}
                            onChangeJson={(json) => updateField('csBOLOsJson', json)}
                        />
                    </FormField>

                    <FormField label="Welfare checks" stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.welfareChecksJson ?? formatPlainTextToHtml(form.welfareChecks || '')}
                            placeholder="Welfare checks..."
                            onChangeText={(text) => updateField('welfareChecks', text)}
                            onChangeJson={(json) => updateField('welfareChecksJson', json)}
                        />
                    </FormField>

                    <FormField label="Noteworthy completed cases, calls, or emails" stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.noteworthyCasesJson ?? formatPlainTextToHtml(form.noteworthyCases || '')}
                            placeholder="Noteworthy items..."
                            onChangeText={(text) => updateField('noteworthyCases', text)}
                            onChangeJson={(json) => updateField('noteworthyCasesJson', json)}
                        />
                    </FormField>

                    <div className="passdown-form__staffing-grid">
                        <div className="passdown-form__staffing-row">
                            <FormField label="Number of abandoned calls during shift" />
                            <Input
                                inputMode="numeric"
                                type="number"
                                value={String(form.abandonedCalls || 0)}
                                onChange={({ detail }) => updateField('abandonedCalls', Math.max(0, parseInt(detail.value) || 0))}
                            />
                        </div>
                        <div className="passdown-form__staffing-row">
                           <FormField label="Average speed of answer during shift (seconds)" />
                            <Input
                                inputMode="numeric"
                                type="number"
                                value={String(form.avgSpeedOfAnswer || 0)}
                                onChange={({ detail }) => updateField('avgSpeedOfAnswer', Math.max(0, parseInt(detail.value) || 0))}
                            />
                        </div>
                    </div>

                    <div className="passdown-form__actions">
                        <div className="passdown-form__actions-buttons">
                            <Button variant="link" onClick={resetForm}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || !hasContent}
                            >
                                {submitting ? 'Submitting...' : (editingId ? 'Update CS Passdown' : 'Submit CS Passdown')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <Spinner />}
            {!loading && passdowns.length === 0 && !showForm && <p className="section-card__placeholder">No CS passdowns yet.</p>}

            {!loading && b0 && (
                <>
                    <div className="passdown-list">
                        <div className="passdown-entry">
                            <div className="passdown-entry__icon">
                                <span className="passdown-entry__meta">
                                    <FormattedDate date={b0.createdAt} /> &middot; by {b0.createdBy}
                                    <span className="badge-custom">
                                        <Badge color="green">Active</Badge>
                                    </span>
                                    { b0.updatedAt && b0.updatedAt !== b0.createdAt && (
                                        <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b0.updatedAt} /> &middot; by {b0.updatedBy}</span>
                                    )}
                                </span>
                                <div className="passdown-entry__actions">
                                    {canModify('csShiftPassdown', b0.createdBy) && (
                                        <ActionButtons
                                            onEdit={() => handleEdit(b0)}
                                            onDelete={() => handleDeleteClick(b0)}
                                            disabled={showForm}
                                        />
                                    )}
                                    <span className="passdown-history__toggle" onClick={() => setActiveExpanded(prev => !prev)}>
                                        <Icon
                                            name={activeExpanded ? "caret-up-filled" : "caret-down-filled"}
                                            variant="subtle"
                                        />
                                    </span>
                                </div>
                            </div>

                            {!activeExpanded && <p style={{marginTop: "8px"}}>{richTextToPlain(b0.openCaseIds) || richTextToPlain(b0.csEscalations)} ...</p>}

                            {activeExpanded && (
                                <div className="passdown-entry__fields-grid">
                                    {renderRichField('Open Case IDs & Next Steps', b0.openCaseIdsJson, b0.openCaseIds)}
                                    {renderRichField('Escalations During Shift', b0.csEscalationsJson, b0.csEscalations)}
                                    {renderRichField('BOLOs', b0.csBOLOsJson, b0.csBOLOs, true)}
                                    {renderRichField('Welfare Checks', b0.welfareChecksJson, b0.welfareChecks)}
                                    {renderRichField('Noteworthy Cases/Calls/Emails', b0.noteworthyCasesJson, b0.noteworthyCases)}
                                    {renderField('Abandoned Calls', b0.abandonedCalls)}
                                    {renderField('Avg Speed of Answer (sec)', b0.avgSpeedOfAnswer)}
                                </div>
                            )}
                        </div>
                    </div>

                    {passdowns.length > 1 && (
                        <div className="passdown-history">
                            <h4 className="passdown-history__title">📋 CS PASSDOWN HISTORY</h4>
                            <div className="passdown-history__list">
                                {(showAllHistory ? passdowns.slice(1) : passdowns.slice(1, 6)).map((b, idx) => {
                                    const isExpanded = expandedId === b.passdownId;
                                    return (
                                        <span key={b.passdownId} style={{marginBottom: "0.8rem"}}>
                                            <Container style={passdownHistoryStyles}>
                                                <span className="passdown-history__dot" />
                                                <div className="passdown-history__content">
                                                    <div className="passdown-history__top">
                                                        <span className="passdown-history__date">
                                                            <FormattedDate date={b.createdAt} /> &middot; by {b.createdBy}
                                                            { b.updatedAt && b.updatedAt !== b.createdAt && (
                                                                <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b.updatedAt} /> &middot; by {b.updatedBy}</span>
                                                            )}
                                                        </span>
                                                        <div className="passdown-history__top-right">
                                                            {canModify('csShiftPassdown', b.createdBy) && (
                                                                <ActionButtons
                                                                    onEdit={() => handleEdit(b)}
                                                                    onDelete={() => handleDeleteClick(b)}
                                                                    disabled={showForm}
                                                                />
                                                            )}
                                                            <span className="passdown-history__toggle" onClick={() => setExpandedId(isExpanded ? null : b.passdownId)}>
                                                                <Icon
                                                                    name={isExpanded ? "caret-up-filled" : "caret-down-filled"}
                                                                    variant="subtle"
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="passdown-history__details">
                                                            <div className="passdown-entry__fields-grid">
                                                                {renderRichField('Open Case IDs & Next Steps', b.openCaseIdsJson, b.openCaseIds)}
                                                                {renderRichField('Escalations During Shift', b.csEscalationsJson, b.csEscalations)}
                                                                {renderRichField('BOLOs', b.csBOLOsJson, b.csBOLOs, true)}
                                                                {renderRichField('Welfare Checks', b.welfareChecksJson, b.welfareChecks)}
                                                                {renderRichField('Noteworthy Cases/Calls/Emails', b.noteworthyCasesJson, b.noteworthyCases)}
                                                                {renderField('Abandoned Calls', b.abandonedCalls)}
                                                                {renderField('Avg Speed of Answer (sec)', b.avgSpeedOfAnswer)}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Container>
                                        </span>
                                    );
                                })}
                            </div>
                            {passdowns.length > 6 && (
                                <div data-size='sm'>
                                    <Button
                                        variant="inline-link"
                                        ariaLabel={showAllHistory ? 'view less' : 'view more'}
                                        onClick={() => setShowAllHistory(prev => {
                                            if (prev) {
                                                setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                                            }
                                            return !prev;
                                        })}
                                    >
                                        {showAllHistory ? 'View less' : 'View more'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {deleteTarget && (
                <Modal
                    visible
                    header="Delete CS Passdown"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                                <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete this CS passdown by <strong>{deleteTarget.createdBy}</strong>? This cannot be undone.
                </Modal>
            )}
        </>
    );
};

export default CSPassdown;
